import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import './NavTest.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faFile, faFolderOpen, faBars, faTimes, faHome, faGraduationCap, faInfoCircle, faWarning } from '@fortawesome/free-solid-svg-icons';
import { ColorsContext } from '../composants/colorContext';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../configurations/axiosInstance/AxiosInstance';
import { formatAuthor } from '../composants/utils/utils';

export default function NavTestNew() {
  const { colors } = useContext(ColorsContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [articleSubMenu, setArticleSubMenu] = useState([]);
  const [partenaires, setPartenaires] = useState([]);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const fetchPartenaires = async () => {
    try {
      const response = await axiosInstance().get('public/api/partenaires/active-published');
      setPartenaires(response.data || []);
    } catch (error) {
      setPartenaires([]);
    }
  };

  const fetchMenus = () => {
    axiosInstance().get('/public/api/menu/WEB_SITE')
      .then(response => {
        let menusJson = response.data.subMenus;
        menusJson.sort((a, b) => a.name.localeCompare(b.name));
        setArticleSubMenu(menusJson);
      })
      .catch(error => { console.error('There was an error!', error); });
  };

  useEffect(() => {
    fetchMenus();
    fetchPartenaires();
  }, []);

  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname || '/');

  useEffect(() => {
    setActivePath(location.pathname || '/');
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleAnchorClick = (e) => {
    const href = e.currentTarget.getAttribute('href') || '';
    try {
      const url = new URL(href, window.location.origin);
      setActivePath(url.pathname);
    } catch (err) {
      setActivePath(href);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const navigateAndClose = (e) => {
    handleAnchorClick(e);
    setMenuOpen(false);
  };

  // Menu sections data
  const menuSections = [
    {
      id: 'accueil',
      label: 'Accueil',
      icon: faHome,
      color: 'indigo',
      href: '/',
      isLink: true,
      description: "",
    },
    {
      id: 'ressources',
      label: 'Ressources',
      icon: faFolderOpen,
      color: 'orange',
      isLink: false,
      description: "Articles, USRAJ, Médias",
    },
    {
      id: 'alert',
      label: 'Alert',
      icon: faWarning,
      color: 'red',
      isLink: false,
      description: "Alertes, Météo",
    },
    {
      id: 'about',
      label: 'À Propos',
      icon: faInfoCircle,
      color: 'blue',
      isLink: false,
      description: "Nous, Partenaires",
    },
    {
      id: 'elearning',
      label: 'E-Learning',
      icon: faGraduationCap,
      color: 'purple',
      href: 'https://e-learning.youthfp.cm',
      isLink: true,
      external: true,
      description: "Cours, Quiz, Certificats",
    },
  ];

  const isActive = (sectionId) => {
    switch (sectionId) {
      case 'accueil': return activePath === '/';
      case 'ressources': return activePath.startsWith('/articles') || activePath.startsWith('/usraj') || activePath.startsWith('/service') || activePath.startsWith('/gallery');
      case 'alert': return activePath.startsWith('/alert') || activePath.startsWith('/weather');
      case 'about': return activePath.startsWith('/about') || activePath.startsWith('/partenaires');
      default: return false;
    }
  };

  const colorMap = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', dot: 'bg-indigo-500' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', dot: 'bg-orange-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', dot: 'bg-blue-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', dot: 'bg-purple-500' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', dot: 'bg-teal-500' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-200', dot: 'bg-pink-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500' },
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-lg border-b border-gray-100 shadow-sm transition-all duration-300">
      <nav className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 w-full">
          {/* Left: YouthFP Logo */}
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="transition-transform hover:scale-105">
                <img src="/images/logo/logo.png" className="block h-8 sm:h-10 w-auto" alt="YouthFP" />
              </a>
            </div>
            {/* UNFPA on mobile only */}
            <div className="flex-shrink-0 flex items-center sm:hidden">
              <a href="https://cameroon.unfpa.org/" className='transition-transform hover:scale-105 no-underline' target="_blank" rel="noopener noreferrer">
                <img src="/images/logo/unfpa.png" className="block h-6 sm:h-8 w-auto" alt="UNFPA" />
              </a>
            </div>
          </div>

          {/* Center: Menu Button */}
          <div className="flex items-center">
            <button
              ref={buttonRef}
              onClick={() => setMenuOpen(!menuOpen)}
              className={`
                relative flex items-center gap-2.5 px-5 py-2.5 rounded-2xl text-sm font-bold
                border-none outline-none cursor-pointer
                transition-all duration-300 ease-out
                ${menuOpen
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200/60 scale-[1.02]'
                  : 'bg-gray-50 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-600 hover:to-purple-600 hover:text-white hover:shadow-lg hover:shadow-indigo-200/60 hover:scale-[1.02]'
                }
              `}
              aria-expanded={menuOpen}
              aria-label="Menu de navigation"
            >
              <div className="w-5 h-4 relative flex flex-col justify-between">
                <span className={`w-full h-0.5 rounded-full transition-all duration-400 ease-out ${menuOpen ? 'bg-white rotate-45 translate-y-[7px]' : 'bg-current'}`}></span>
                <span className={`w-3/4 h-0.5 rounded-full transition-all duration-300 ease-out ${menuOpen ? 'bg-white opacity-0 scale-x-0' : 'bg-current'}`}></span>
                <span className={`w-full h-0.5 rounded-full transition-all duration-400 ease-out ${menuOpen ? 'bg-white -rotate-45 -translate-y-[7px]' : 'bg-current'}`}></span>
              </div>
              <span className="hidden sm:inline">Menu</span>
              <FontAwesomeIcon icon={faChevronDown} className={`text-[10px] transition-transform duration-300 ${menuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Right: UNFPA on Desktop */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-shrink-0 items-center">
              <a href="https://cameroon.unfpa.org/" className="transition-transform hover:scale-105 no-underline" target="_blank" rel="noopener noreferrer">
                <img src="/images/logo/unfpa.png" className="block h-10 w-auto" alt="UNFPA" />
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-gray-900/20 backdrop-blur-sm z-40 transition-opacity duration-300 ${menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
        onClick={() => setMenuOpen(false)}
        style={{ top: '80px' }}
      ></div>

      {/* Dropdown Panel */}
      <div
        ref={menuRef}
        className={`
          absolute left-0 right-0 z-50 
          transition-all duration-400 ease-out origin-top
          ${menuOpen ? 'opacity-100 translate-y-0 scale-y-100 visible' : 'opacity-0 -translate-y-2 scale-y-[0.97] invisible pointer-events-none'}
        `}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="bg-white border border-gray-100 rounded-3xl shadow-2xl shadow-gray-200/80 overflow-hidden">

            {/* Menu sections grid */}
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

                {menuSections.map((section) => {
                  const colors = colorMap[section.color];
                  const active = isActive(section.id);
                  const expanded = expandedSection === section.id;

                  if (section.isLink) {
                    // Direct link sections (Accueil, E-Learning)
                    return (
                      <a
                        key={section.id}
                        href={section.href}
                        onClick={section.external ? () => setMenuOpen(false) : navigateAndClose}
                        target={section.external ? '_blank' : undefined}
                        rel={section.external ? 'noopener noreferrer' : undefined}
                        className={`
                          group flex items-center gap-3 p-4 rounded-2xl no-underline
                          transition-all duration-300 ease-out
                          ${active
                            ? `${colors.bg} ${colors.text} shadow-sm`
                            : 'bg-gray-50/80 text-gray-700 hover:bg-gray-100 hover:shadow-sm hover:scale-[1.01]'
                          }
                        `}
                      >
                        <div className={`
                          w-10 h-10 rounded-xl flex items-center justify-center text-sm
                          transition-all duration-300
                          ${active
                            ? `${colors.bg} ${colors.text}`
                            : `bg-white text-gray-400 group-hover:${colors.text} shadow-sm`
                          }
                        `}>
                          <FontAwesomeIcon icon={section.icon} />
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-sm">{section.label}</span>
                          <span className="block text-xs text-gray-400 mt-0.5">
                            {section.description}
                          </span>
                        </div>
                        <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-all group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </a>
                    );
                  }

                  // Expandable sections (Ressources, À Propos)
                  return (
                    <div key={section.id} className="flex flex-col">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`
                          group flex items-center gap-3 p-4 rounded-2xl w-full text-left
                          border-none outline-none cursor-pointer
                          transition-all duration-300 ease-out
                          ${active
                            ? `${colors.bg} ${colors.text} shadow-sm`
                            : expanded
                              ? 'bg-gray-100 text-gray-800 shadow-sm'
                              : 'bg-gray-50/80 text-gray-700 hover:bg-gray-100 hover:shadow-sm hover:scale-[1.01]'
                          }
                        `}
                      >
                        <div className={`
                          w-10 h-10 rounded-xl flex items-center justify-center text-sm
                          transition-all duration-300
                          ${active
                            ? `${colors.bg} ${colors.text}`
                            : `bg-white text-gray-400 shadow-sm`
                          }
                        `}>
                          <FontAwesomeIcon icon={section.icon} />
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-sm">{section.label}</span>
                          <span className="block text-xs text-gray-400 mt-0.5">
                            {section.description}
                          </span>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-[10px] text-gray-400 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Expanded Sub-sections */}
              {/* Ressources Expanded */}
              <div className={`transition-all duration-400 ease-out overflow-hidden ${expandedSection === 'ressources' ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                <div className="bg-gradient-to-br from-gray-50 to-orange-50/30 rounded-2xl p-5 sm:p-6 border border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* Articles */}
                    {articleSubMenu.length !== 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-500 flex items-center justify-center text-xs">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">Articles</h3>
                        </div>
                        <ul className="space-y-1.5 p-0 m-0 list-none">
                          {articleSubMenu.map((menu) => (
                            <li key={menu.name}>
                              <a
                                onClick={navigateAndClose}
                                className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath === `/articles/${menu.id}` ? 'text-orange-600 bg-orange-50 font-semibold' : 'text-gray-500 hover:text-orange-500 hover:bg-white'}`}
                                href={`/articles/${menu.id}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath === `/articles/${menu.id}` ? 'bg-orange-500' : 'bg-gray-300'}`}></span>
                                {formatAuthor(menu.description, 40)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* USRAJ */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-500 flex items-center justify-center text-xs font-bold">
                          U
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">USRAJ</h3>
                      </div>
                      <ul className="space-y-1.5 p-0 m-0 list-none">
                        <li>
                          <a onClick={navigateAndClose} href="/usraj/location" className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath.startsWith('/usraj') ? 'text-teal-600 bg-teal-50 font-semibold' : 'text-gray-500 hover:text-teal-500 hover:bg-white'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath.startsWith('/usraj') ? 'bg-teal-500' : 'bg-gray-300'}`}></span>
                            Localisation des USRAJ
                          </a>
                        </li>
                        <li>
                          <a onClick={navigateAndClose} href="/service" className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath.startsWith('/service') ? 'text-teal-600 bg-teal-50 font-semibold' : 'text-gray-500 hover:text-teal-500 hover:bg-white'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath.startsWith('/service') ? 'bg-teal-500' : 'bg-gray-300'}`}></span>
                            Services
                          </a>
                        </li>
                      </ul>
                    </div>

                    {/* Médiathèque */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-500 flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">Médiathèque</h3>
                      </div>
                      <div
                        className="group block relative rounded-xl overflow-hidden cursor-pointer border border-gray-200 hover:border-purple-200 transition-all hover:shadow-md"
                        onClick={(e) => { e.preventDefault(); handleAnchorClick({ currentTarget: { getAttribute: () => '/gallery' } }); setMenuOpen(false); window.location.href = '/gallery'; }}
                      >
                        <img src="/images/images.png" alt="Médiathèque" className="w-full h-28 object-cover transform group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent flex items-end p-3">
                          <span className="text-white text-sm font-semibold flex items-center gap-2 group-hover:text-purple-200 transition-colors">
                            Découvrir
                            <svg className="w-3.5 h-3.5 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alert Expanded */}
              <div className={`transition-all duration-400 ease-out overflow-hidden ${expandedSection === 'alert' ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                <div className="bg-gradient-to-br from-gray-50 to-orange-50/30 rounded-2xl p-5 sm:p-6 border border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* Alert */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-500 flex items-center justify-center text-xs font-bold">
                          <FontAwesomeIcon icon={faWarning} />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">Alertes</h3>
                      </div>
                      <ul className="space-y-1.5 p-0 m-0 list-none">
                        <li>
                          <a onClick={navigateAndClose} href="/alert/all" className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath.startsWith('/alert/all') ? 'text-teal-600 bg-teal-50 font-semibold' : 'text-gray-500 hover:text-teal-500 hover:bg-white'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath.startsWith('/alert') ? 'bg-teal-500' : 'bg-gray-300'}`}></span>
                            Les Alertes
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* À Propos Expanded */}
              <div className={`transition-all duration-400 ease-out overflow-hidden ${expandedSection === 'about' ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-5 sm:p-6 border border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                    {/* Nous */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-500 flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">Nous</h3>
                      </div>
                      <ul className="space-y-1.5 p-0 m-0 list-none">
                        <li>
                          <a onClick={navigateAndClose} href='/about' className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath === '/about' ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-blue-500 hover:bg-white'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath === '/about' ? 'bg-blue-500' : 'bg-gray-300'}`}></span>
                            Qui sommes nous ?
                          </a>
                        </li>
                      </ul>
                    </div>

                    {/* Partenaires */}
                    {partenaires.length !== 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-500 flex items-center justify-center">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                          </div>
                          <h3 className="text-sm font-bold text-gray-800 m-0 uppercase tracking-wide">Partenaires</h3>
                        </div>
                        <ul className="space-y-1.5 p-0 m-0 list-none">
                          {partenaires.map((partenaire) => (
                            <li key={partenaire.name}>
                              <a onClick={navigateAndClose} className={`flex items-center gap-2 text-sm py-1.5 px-3 rounded-lg transition-all no-underline ${activePath.startsWith('/partenaires') ? 'text-pink-600 bg-pink-50 font-semibold' : 'text-gray-500 hover:text-pink-500 hover:bg-white'}`} href={`/partenaires`}>
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${activePath.startsWith('/partenaires') ? 'bg-pink-500' : 'bg-gray-300'}`}></span>
                                {formatAuthor(partenaire.description || partenaire.name, 25)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
